/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React, {useRef} from 'react'
import {useIntl, FormattedMessage} from 'react-intl'
import {Box, Flex, Text, useMultiStyleConfig} from '@chakra-ui/react'
import Link from '../../../components/link'
import {BrandLogo, LockIcon} from '../../../components/icons'
import {useCurrentCustomer} from '../../../hooks/use-current-customer'
import {ACCOUNT_HREF, HOME_HREF} from '../../../constants'
import textStyles from '../../../theme/foundations/textStyles'
import PropTypes from 'prop-types'
import {css, Global} from '@emotion/react'

const CheckoutHeader = ({isCheckoutConfirmation = false}) => {
    const intl = useIntl()
    const {data: customer} = useCurrentCustomer()
    const brandText = intl.formatMessage({defaultMessage: 'STOKKE Logo', id: 'global.brand_logo'})
    const lockIconText = intl.formatMessage({
        defaultMessage: 'Lock Icon',
        id: 'checkout-header.label.lock_icon'
    })
    const styles = useMultiStyleConfig('Header', {variant: 'checkout'})
    const showAccountLink = customer.isRegistered && isCheckoutConfirmation
    const ref = useRef(null)

    return (
        <Box bg="white" as="header">
            {/* expose the component height as css var - currently used for top positioned toasts */}
            <Global
                styles={css`
                    :root {
                        --header-height: ${ref?.current?.clientHeight || 0}px;
                    }
                `}
            />

            <Box {...styles.mainContainer} ref={ref}>
                <Flex width="100%" height="88px" align="center" justify="space-between">
                    <Link href={HOME_HREF} title="Back to homepage" variant="image">
                        <BrandLogo
                            width={{base: '56px'}}
                            height={{base: '56px'}}
                            aria-label={brandText}
                        />
                    </Link>

                    <Flex direction="row">
                        {showAccountLink ? (
                            <Link {...textStyles['Regular Regular Medium']} to={ACCOUNT_HREF}>
                                <FormattedMessage
                                    defaultMessage="My Stokke"
                                    id="global.my_account"
                                />
                            </Link>
                        ) : (
                            <>
                                <Text {...textStyles['Semi Bold Small']} mt="2px">
                                    <FormattedMessage
                                        defaultMessage="Secure Checkout"
                                        id="global.secure_checkout"
                                    />
                                </Text>
                                <LockIcon
                                    width={{base: '16px'}}
                                    height={{base: '16px'}}
                                    ml="6px"
                                    role="img"
                                    aria-label={lockIconText}
                                    aria-hidden="true"
                                    data-testid="lock-logo"
                                />
                            </>
                        )}
                    </Flex>
                </Flex>
            </Box>
        </Box>
    )
}

CheckoutHeader.propTypes = {
    isCheckoutConfirmation: PropTypes.bool
}

export default CheckoutHeader
